import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type { ApiEnvelope, AuthenticationResult, PortalSession, PrivacyConsentPending } from "./types";

type PortalRequestConfig = AxiosRequestConfig & {
  skipAuth?: boolean;
  skipRefresh?: boolean;
  retriedAfterRefresh?: boolean;
};

const backendUrl = String(import.meta.env.VITE_DOMAIN_BACKEND ?? "").replace(
  /\/+$/,
  ""
);

export function getPortalAssetUrl(path: string | null | undefined) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${backendUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

export const portalApi = axios.create({
  baseURL: backendUrl,
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

const refreshApi = axios.create({
  baseURL: backendUrl,
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

let accessToken: string | null = null;
let refreshPromise: Promise<AuthenticationResult> | null = null;
let sessionExpiredHandler: (() => void) | null = null;
let consentHandler: ((pending: PrivacyConsentPending) => void) | null = null;

export function setPortalAccessToken(token: string | null) {
  accessToken = token;
}

export function onPortalSessionExpired(handler: (() => void) | null) {
  sessionExpiredHandler = handler;
}
export function onPortalPrivacyConsentRequired(handler: ((pending: PrivacyConsentPending) => void) | null) { consentHandler = handler; }

export function getApiErrorMessage(
  error: unknown,
  fallback = "Não foi possível concluir a operação."
) {
  if (axios.isAxiosError(error)) {
    const message = (error.response?.data as { message?: unknown } | undefined)
      ?.message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

export function refreshPortalSession() {
  if (!refreshPromise) {
    refreshPromise = refreshApi
      .post<ApiEnvelope<AuthenticationResult>>("/portal/auth/refresh")
      .then(({ data }) => {
        if ('requiresPrivacyConsent' in data.data) { setPortalAccessToken(null); consentHandler?.(data.data); }
        else if (!data.data.requiresSupplierSelection) setPortalAccessToken(data.data.accessToken);
        return data.data;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

portalApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const request = config as InternalAxiosRequestConfig & PortalRequestConfig;
  if (!request.skipAuth && accessToken) {
    request.headers.set("Authorization", `Bearer ${accessToken}`);
  }
  request.headers.set('x-owlen-locale', window.localStorage.getItem('owlen:language') || navigator.language || 'pt-BR');
  return config;
});

portalApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const request = error.config as
      | (InternalAxiosRequestConfig & PortalRequestConfig)
      | undefined;
    if (
      error.response?.status !== 401 ||
      !request ||
      request.skipRefresh ||
      request.retriedAfterRefresh
    ) {
      throw error;
    }

    request.retriedAfterRefresh = true;
    try {
      const session = await refreshPortalSession();
      if ('requiresPrivacyConsent' in session || session.requiresSupplierSelection) throw error;
      request.headers.set("Authorization", `Bearer ${session.accessToken}`);
      return portalApi.request(request);
    } catch (refreshError) {
      setPortalAccessToken(null);
      sessionExpiredHandler?.();
      throw refreshError;
    }
  }
);
